/**
 * tar, in as much of it as the asset set uses.
 *
 * The set is delivered to a browser as a handful of gzipped tar archives, and
 * this is both ends of that: `tarHeader`/`tarEnd` build one, `TarReader` takes
 * one apart as the bytes arrive. Writing it rather than depending on a package
 * is worth it here because the reader has to be incremental — a 200 MB archive
 * is unpacked into the filesystem while it downloads, not after — and because
 * the two halves being the same file is what keeps them agreeing.
 *
 * What is supported is what the packer emits: regular files, directories,
 * symlinks, and GNU long names for the paths that do not fit in the 100-byte
 * name field, which in ESP-IDF is thousands of them. Anything else in an
 * archive is an error rather than something to skip, on the grounds that a
 * silently missing file becomes a compile error hundreds of steps later.
 */

export const BLOCK = 512;

export interface TarEntry {
    path: string;
    kind: "file" | "dir" | "symlink";
    /** Modification time in milliseconds, as everything else here uses. */
    mtimeMs: number;
    mode: number;
    /** For a file, its length; zero otherwise. */
    size: number;
    /** For a symlink, what it points at. */
    target: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const Type = { FILE: 0x30, DIR: 0x35, SYMLINK: 0x32, LONGNAME: 0x4c } as const;

/**
 * The header blocks for one entry: the entry itself, and a GNU long name
 * record in front of it when the path does not fit.
 *
 * The file's own bytes are not here — the packer streams them straight from
 * disk — so the caller pads them to a block boundary itself. `padding` says by
 * how much.
 */
export function tarHeader(entry: TarEntry): Uint8Array {
    const name = encoder.encode(entry.path);
    if (name.length <= 100) return header(entry, entry.path);

    // A GNU long name: a header of its own whose body is the path, followed by
    // the real header carrying a truncated copy. The truncated one is never
    // read by anything that understands the extension, and is what a tar that
    // does not would fall back to.
    const long = header(
        { ...entry, kind: "file", size: name.length, mode: 0o644, target: "" },
        "././@LongLink",
        Type.LONGNAME,
    );
    const body = new Uint8Array(blocks(name.length) * BLOCK);
    body.set(name);
    const real = header(entry, decoder.decode(name.subarray(0, 100)));
    return concat([long, body, real]);
}

/** The two zero blocks that end an archive. */
export function tarEnd(): Uint8Array {
    return new Uint8Array(2 * BLOCK);
}

/** How many bytes of padding follow a body of this length. */
export function padding(size: number): number {
    return (BLOCK - (size % BLOCK)) % BLOCK;
}

function blocks(size: number): number {
    return Math.ceil(size / BLOCK);
}

function header(entry: TarEntry, path: string, typeflag?: number): Uint8Array {
    const block = new Uint8Array(BLOCK);
    const put = (offset: number, text: string) => block.set(encoder.encode(text), offset);
    // Octal, NUL-terminated, zero-padded: the format's one numeric encoding.
    const octal = (offset: number, width: number, value: number) =>
        put(offset, value.toString(8).padStart(width - 1, "0"));

    put(0, path);
    octal(100, 8, entry.mode & 0o7777);
    octal(108, 8, 0); // uid
    octal(116, 8, 0); // gid
    octal(124, 12, entry.kind === "file" ? entry.size : 0);
    octal(136, 12, Math.floor(entry.mtimeMs / 1000));
    block[156] = typeflag ?? (entry.kind === "dir" ? Type.DIR : entry.kind === "symlink" ? Type.SYMLINK : Type.FILE);
    put(157, entry.target);
    put(257, "ustar  "); // GNU's magic and version, which is what long names are
    put(265, "root");
    put(297, "root");

    // The checksum is computed with its own field read as spaces, then written
    // into it. Six octal digits, a NUL and a space, which is what every tar
    // writes whatever the spec allows.
    block.fill(0x20, 148, 156);
    let sum = 0;
    for (const byte of block) sum += byte;
    put(148, sum.toString(8).padStart(6, "0"));
    block[154] = 0;
    block[155] = 0x20;
    return block;
}

function concat(parts: Uint8Array[]): Uint8Array {
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let at = 0;
    for (const part of parts) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}

/**
 * An archive taken apart as its bytes arrive.
 *
 * `push` is called with whatever a fetch handed over, in whatever sizes it
 * arrived in, and calls back for each entry and for each piece of its body.
 * Nothing is buffered beyond the block being assembled and the body of a long
 * name — a 200 MB archive costs one 512-byte block of state.
 *
 * The body is handed over in pieces because that is how it arrives; a caller
 * that wants a whole file joins them. Writing into a filesystem does not: it
 * writes each piece at the offset it came from.
 */
export interface TarSink {
    entry(entry: TarEntry): void;
    /** Part of the current file's body, at `offset` within it. */
    data(chunk: Uint8Array, offset: number): void;
    /** The current file has been handed over in full. */
    endOfEntry(entry: TarEntry): void;
}

export class TarReader {
    readonly #sink: TarSink;
    /** A partial 512-byte block, or the header we are still assembling. */
    #block = new Uint8Array(BLOCK);
    #blockAt = 0;
    /** The entry whose body we are in the middle of, if any. */
    #entry: TarEntry | undefined;
    #bodyLeft = 0;
    #bodyAt = 0;
    #padLeft = 0;
    /** A GNU long name waiting for the header it belongs to. */
    #longName: Uint8Array | undefined;
    #longAt = 0;

    constructor(sink: TarSink) {
        this.#sink = sink;
    }

    push(chunk: Uint8Array): void {
        let at = 0;
        while (at < chunk.length) {
            if (this.#bodyLeft > 0) {
                const n = Math.min(this.#bodyLeft, chunk.length - at);
                const piece = chunk.subarray(at, at + n);
                if (this.#longName !== undefined) {
                    this.#longName.set(piece, this.#longAt);
                    this.#longAt += n;
                } else {
                    this.#sink.data(piece, this.#bodyAt);
                }
                this.#bodyAt += n;
                this.#bodyLeft -= n;
                at += n;
                if (this.#bodyLeft === 0 && this.#longName === undefined && this.#entry) {
                    this.#sink.endOfEntry(this.#entry);
                    this.#entry = undefined;
                }
                continue;
            }
            if (this.#padLeft > 0) {
                const n = Math.min(this.#padLeft, chunk.length - at);
                this.#padLeft -= n;
                at += n;
                continue;
            }
            const n = Math.min(BLOCK - this.#blockAt, chunk.length - at);
            this.#block.set(chunk.subarray(at, at + n), this.#blockAt);
            this.#blockAt += n;
            at += n;
            if (this.#blockAt === BLOCK) {
                this.#blockAt = 0;
                this.#header();
            }
        }
    }

    /** Everything the archive held has been pushed. */
    end(): void {
        if (this.#bodyLeft > 0) throw new Error("the archive ends inside a file");
    }

    #header(): void {
        // Two zero blocks end the archive; a single one is padding some tars
        // add, and either way there is nothing to read out of it.
        if (this.#block.every((byte) => byte === 0)) return;
        checksum(this.#block);

        const type = this.#block[156]!;
        const size = octal(this.#block, 124, 12);

        if (type === Type.LONGNAME) {
            this.#longName = new Uint8Array(size);
            this.#longAt = 0;
            this.#bodyLeft = size;
            this.#bodyAt = 0;
            this.#padLeft = padding(size);
            return;
        }

        const path = this.#longName ? decoder.decode(trimNul(this.#longName)) : text(this.#block, 0, 100);
        this.#longName = undefined;

        const kind =
            type === Type.DIR
                ? "dir"
                : type === Type.SYMLINK
                  ? "symlink"
                  : type === Type.FILE || type === 0
                    ? "file"
                    : undefined;
        if (kind === undefined) {
            const named = String.fromCharCode(type);
            throw new Error(`tar entry ${path} is of unsupported type ${named}`);
        }

        const entry: TarEntry = {
            path,
            kind,
            mode: octal(this.#block, 100, 8),
            size: kind === "file" ? size : 0,
            mtimeMs: octal(this.#block, 136, 12) * 1000,
            target: kind === "symlink" ? text(this.#block, 157, 100) : "",
        };
        this.#sink.entry(entry);

        if (kind === "file" && size > 0) {
            this.#entry = entry;
            this.#bodyLeft = size;
            this.#bodyAt = 0;
            this.#padLeft = padding(size);
        } else {
            this.#sink.endOfEntry(entry);
        }
    }
}

function octal(block: Uint8Array, offset: number, width: number): number {
    const value = parseInt(text(block, offset, width).trim() || "0", 8);
    if (Number.isNaN(value)) throw new Error("a tar header holds something that is not a number");
    return value;
}

function text(block: Uint8Array, offset: number, width: number): string {
    return decoder.decode(trimNul(block.subarray(offset, offset + width)));
}

function trimNul(bytes: Uint8Array): Uint8Array {
    const end = bytes.indexOf(0);
    return end === -1 ? bytes : bytes.subarray(0, end);
}

/**
 * Check a header against the checksum in it.
 *
 * Worth the pass over 512 bytes: the failure it catches — a stream that has
 * lost sync, from a truncated download or a bug here — otherwise shows up as a
 * path of random bytes, or as a size that makes the reader skip the rest of the
 * archive.
 */
function checksum(block: Uint8Array): void {
    const want = octal(block, 148, 8);
    let sum = 0;
    for (let i = 0; i < BLOCK; i++) sum += i >= 148 && i < 156 ? 0x20 : block[i]!;
    if (sum !== want) throw new Error(`tar header checksum is ${sum}, not the ${want} it claims`);
}
